import { Alert, Autocomplete, Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ConfigLabelSelect } from '../adapter/ConfigLabelSelect';
import { adapterServiceApi } from '../../infrastructure/api/adapterServiceApi';
import { useAdapterConfigImportSources } from '../../hooks/useAdapterServices';
import { messageFromAdminConsoleAxiosError } from '../../lib/axiosErrorMessage';
import type { CanonicalFieldDef } from '../../domain/canonicalFieldDef';

export type AdapterContractApplyPayload = {
  canonicalRequest: Record<string, unknown>;
  canonicalResponse: Record<string, unknown>;
  canonicalRequestFields: CanonicalFieldDef[];
  canonicalResponseFields: CanonicalFieldDef[];
  source: 'adapter-config' | 'registry';
};

type Props = {
  serviceId: string;
  adapterServiceKey: string;
  defaultConfigLabel: string;
  capability: string;
  onApply: (payload: AdapterContractApplyPayload) => void;
};

export function AdapterConfigContractPicker({
  serviceId,
  adapterServiceKey,
  defaultConfigLabel,
  capability,
  onApply,
}: Props) {
  const { data: importSources = [], isLoading: sourcesLoading } = useAdapterConfigImportSources();
  const [importLabel, setImportLabel] = useState(defaultConfigLabel || 'default');
  const [importAdapterKey, setImportAdapterKey] = useState(adapterServiceKey);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setImportLabel(defaultConfigLabel || 'default');
  }, [defaultConfigLabel]);

  useEffect(() => {
    setImportAdapterKey(adapterServiceKey);
  }, [adapterServiceKey]);

  const keysForLabel = useMemo(() => {
    const match = importSources.find(s => s.label === importLabel);
    if (match?.adapterServiceKeys.length) return match.adapterServiceKeys;
    if (importAdapterKey) {
      return importSources.flatMap(s => s.adapterServiceKeys).includes(importAdapterKey)
        ? [importAdapterKey]
        : importAdapterKey
          ? [importAdapterKey]
          : [];
    }
    return [];
  }, [importSources, importLabel, importAdapterKey]);

  useEffect(() => {
    if (keysForLabel.length && !keysForLabel.includes(importAdapterKey)) {
      setImportAdapterKey(keysForLabel[0]);
    }
  }, [keysForLabel, importAdapterKey]);

  const cap = capability.trim();
  const canFetch = Boolean(serviceId && cap && importLabel.trim() && importAdapterKey.trim());
  const sourceHint = importSources.find(s => s.label === importLabel);

  const applyWithConfirm = async () => {
    if (!canFetch) return;
    if (
      !window.confirm(
        'Replace request/response field catalogs from adapter config JSONata? Required/optional flags you edited may change.',
      )
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const contract = await adapterServiceApi.getAdapterConfigContract(serviceId, cap, {
        label: importLabel.trim(),
        importAdapterServiceKey: importAdapterKey.trim(),
      });
      onApply({
        canonicalRequest: contract.canonicalRequest,
        canonicalResponse: contract.canonicalResponse,
        canonicalRequestFields: contract.canonicalRequestFields,
        canonicalResponseFields: contract.canonicalResponseFields,
        source: 'adapter-config',
      });
    } catch (e) {
      setError(messageFromAdminConsoleAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Apply adapter contract (executeService)</Typography>
      <Alert severity="warning">
        Bootstrap from adapter config or registry — not authoritative. Request fields are <code>p.*</code>{' '}
        paths from provider request JSONata; response fields are normalized keys from provider response
        JSONata. Review against backend <code>executeService</code> call sites before publish. Vendor wire
        fields (<code>Username</code>, <code>tenant_id</code>, etc.) belong in Provider Onboarding.
      </Alert>
      <ConfigLabelSelect
        selectOnly
        value={importLabel}
        onChange={setImportLabel}
        helperText="adapter_configs.label with synced provider mappings (from Provider Onboarding → Sync), not catalog-only publish."
      />
      <Autocomplete
        freeSolo
        loading={sourcesLoading}
        options={keysForLabel}
        value={importAdapterKey}
        onChange={(_, v) => setImportAdapterKey(String(v ?? '').trim().toLowerCase())}
        onInputChange={(_, v, reason) => {
          if (reason === 'input') setImportAdapterKey(v.trim().toLowerCase());
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Import adapter service key"
            helperText="config:service:{key} row under the selected label (can differ from this service row's key)."
            required
          />
        )}
      />
      <Button variant="contained" disabled={!canFetch || loading} onClick={() => void applyWithConfirm()}>
        {loading ? 'Loading…' : 'Apply from adapter config'}
      </Button>
      {!cap && (
        <Typography variant="body2" color="text.secondary">
          Enter a capability ID on the Capabilities step before applying a contract.
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Service row key <code>{adapterServiceKey || '—'}</code>
        {sourceHint && sourceHint.adapterServiceKeys.length > 0
          ? ` · label "${importLabel}" has keys: ${sourceHint.adapterServiceKeys.map(k => `\`${k}\``).join(', ')}`
          : sourcesLoading
            ? ' · loading import sources…'
            : ` · no importable provider mappings for label "${importLabel}" (sync vendors under this label first)`}
      </Typography>
    </Stack>
  );
}
